/**
 * @voltxampere/engine — Formula detector orchestrator (Sprint 56b)
 *
 * VISION §6.G P0: holographic formula overlay. Runs every registered
 * template against the circuit and returns the matches + best-fit
 * (highest specificity wins).
 *
 * Adding a new template:
 *   1. Create `templates/<id>.ts` exporting a FormulaTemplate.
 *   2. Add the literal to FormulaTemplateId union in `types.ts`.
 *   3. Register in ALL_TEMPLATES below (specificity order is
 *      preserved by the registry).
 *   4. Add i18n entries `formula.<id>.{name}` in the web app i18n
 *      bundles (apps/web/src/i18n/{tr,en}.ts).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { bjtCommonEmitterTemplate } from "./templates/bjt-common-emitter.js";
import { ledCurrentLimitTemplate } from "./templates/led-current-limit.js";
import { opampInvertingTemplate } from "./templates/opamp-inverting.js";
import { opampNonInvertingTemplate } from "./templates/opamp-non-inverting.js";
import { rcFilterTemplate } from "./templates/rc-filter.js";
import { voltageDividerTemplate } from "./templates/voltage-divider.js";
import { pickBestMatch } from "./helpers.js";
/**
 * S56b MVP template registry. Specificity tier is intrinsic to each
 * template; the orchestrator uses it to resolve overlap.
 */
export const ALL_TEMPLATES = Object.freeze([
    // High-specificity templates first so the "first match" search
    // surfaces the most specific recognition.
    opampInvertingTemplate,
    opampNonInvertingTemplate,
    bjtCommonEmitterTemplate,
    // Medium-specificity.
    rcFilterTemplate,
    ledCurrentLimitTemplate,
    // Low-specificity catch-all.
    voltageDividerTemplate,
]);
/**
 * Run every registered template. `selectedComponentIds` scopes the
 * search to the user's current selection — pass an empty set or
 * undefined to search the whole circuit.
 *
 * Never throws. Each template's `match` is pure; failures inside
 * a template propagate up unmasked so the caller sees the actual
 * error (no silent swallow).
 */
export function detectFormulas(circuit, selectedComponentIds) {
    const t0 = performance.now();
    const matches = [];
    for (const template of ALL_TEMPLATES) {
        const m = template.match(circuit, selectedComponentIds);
        if (m !== null)
            matches.push(m);
    }
    const best = pickBestMatch(matches);
    const t1 = performance.now();
    return {
        matches,
        best,
        durationMs: t1 - t0,
    };
}
//# sourceMappingURL=index.js.map